import React from 'react';
import Icon from 'components/AppIcon';
import GoalAIPrediction from './GoalAIPrediction';

const GOAL_TYPE_CONFIG = {
  vacation: { icon: 'Plane', color: '#00E5FF', label: 'Vacation' },
  emergency_fund: { icon: 'Shield', color: '#7B2FBE', label: 'Emergency Fund' },
  debt_repayment: { icon: 'CreditCard', color: '#F59E0B', label: 'Debt Repayment' },
  home_down_payment: { icon: 'Home', color: '#10B981', label: 'Home Down Payment' },
  custom: { icon: 'Target', color: '#06B6D4', label: 'Custom' },
};

export default function GoalCard({ goal, onContribute, onEdit, onDelete, onClick }) {
  const target = parseFloat(goal?.target_amount) || 1;
  const current = parseFloat(goal?.current_amount) || 0;
  const pct = Math.min((current / target) * 100, 100);
  const typeKey = goal?.type || 'custom';
  const config = GOAL_TYPE_CONFIG?.[typeKey] || GOAL_TYPE_CONFIG?.custom;
  const color = goal?.color || config?.color;
  const iconName = goal?.icon || config?.icon;

  const getDaysLeft = () => {
    if (!goal?.deadline) return null;
    const diff = new Date(goal.deadline) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getProjectedCompletion = () => {
    const monthly = parseFloat(goal?.monthly_contribution) || 0;
    if (monthly <= 0) return null;
    const remaining = target - current;
    if (remaining <= 0) return 'Completed!';
    const monthsNeeded = Math.ceil(remaining / monthly);
    const projDate = new Date();
    projDate?.setMonth(projDate?.getMonth() + monthsNeeded);
    return projDate?.toLocaleDateString('default', { month: 'short', year: 'numeric' });
  };

  const getStatusBadge = () => {
    if (pct >= 100) return { label: 'Completed', color: '#10B981', bg: 'rgba(16,185,129,0.12)' };
    if (!goal?.deadline) return null;
    const daysLeft = getDaysLeft();
    const monthly = parseFloat(goal?.monthly_contribution) || 0;
    const remaining = target - current;
    const monthsLeft = daysLeft / 30;
    if (monthly * monthsLeft >= remaining) return { label: 'On Track', color: '#10B981', bg: 'rgba(16,185,129,0.12)' };
    if (monthly * monthsLeft >= remaining * 0.8) return { label: 'Behind', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' };
    return { label: 'At Risk', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' };
  };

  const daysLeft = getDaysLeft();
  const projected = getProjectedCompletion();
  const status = getStatusBadge();

  return (
    <div
      className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-5 hover:border-[rgba(6,182,212,0.25)] transition-all duration-300 cursor-pointer group relative"
      onClick={() => onClick?.(goal)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}20`, border: `1px solid ${color}30` }}
          >
            <Icon name={iconName} size={20} color={color} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-sm text-[#F8FAFC] leading-tight">{goal?.title}</h3>
            <p className="text-xs text-[#64748B] mt-0.5">{config?.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e?.stopPropagation(); onEdit?.(goal); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[rgba(6,182,212,0.1)] transition-colors"
          >
            <Icon name="Pencil" size={13} color="#64748B" />
          </button>
          <button
            onClick={(e) => { e?.stopPropagation(); onDelete?.(goal); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[rgba(239,68,68,0.1)] transition-colors"
          >
            <Icon name="Trash2" size={13} color="#64748B" />
          </button>
        </div>
      </div>
      {/* Status Badge */}
      {status && (
        <div className="mb-3">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ color: status?.color, backgroundColor: status?.bg }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status?.color }} />
            {status?.label}
          </span>
        </div>
      )}
      {/* Amounts */}
      <div className="flex items-end justify-between mb-2">
        <div>
          <span className="font-data text-lg font-bold text-[#F8FAFC]">
            ${current?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
          <span className="text-xs text-[#64748B] ml-1">
            / ${target?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
        <span className="font-data text-sm font-semibold" style={{ color }}>
          {pct?.toFixed(1)}%
        </span>
      </div>
      {/* Progress Bar */}
      <div className="h-2 bg-[#1E293B] rounded-full overflow-hidden mb-4">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}99, ${color})` }}
        />
      </div>
      {/* Meta */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {daysLeft !== null && (
          <div className="bg-[#0A1628] rounded-lg p-2">
            <p className="text-[10px] text-[#64748B] mb-0.5">Deadline</p>
            <p className="font-data text-xs text-[#94A3B8] font-medium">
              {daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
            </p>
          </div>
        )}
        {goal?.monthly_contribution > 0 && (
          <div className="bg-[#0A1628] rounded-lg p-2">
            <p className="text-[10px] text-[#64748B] mb-0.5">Monthly</p>
            <p className="font-data text-xs font-medium" style={{ color }}>
              ${parseFloat(goal?.monthly_contribution)?.toFixed(0)}/mo
            </p>
          </div>
        )}
        {projected && (
          <div className="bg-[#0A1628] rounded-lg p-2">
            <p className="text-[10px] text-[#64748B] mb-0.5">Projected</p>
            <p className="font-data text-xs text-[#94A3B8] font-medium">{projected}</p>
          </div>
        )}
      </div>
      {/* AI Prediction chip */}
      {goal?.goal_status === 'active' && goal?.monthly_contribution > 0 && (
        <div className="mb-3">
          <GoalAIPrediction goal={goal} />
        </div>
      )}
      {/* Quick Contribute */}
      <button
        onClick={(e) => { e?.stopPropagation(); onContribute?.(goal); }}
        className="w-full py-2 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center justify-center gap-1.5"
        style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${color}25`; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${color}15`; }}
      >
        <Icon name="Plus" size={13} color={color} />
        Quick Contribute
      </button>
    </div>
  );
}
